<template>
	<div class="points-claim-wrapper">
		<v-btn v-if="!readonly || claimed" class="points-claim-btn" @click="openDialog" :disabled="claimed || points <= 0">
			<md-icon v-if="claimed">check</md-icon>
			<md-icon v-else>paid</md-icon>
			<span class="points-claim-btn__text" v-if="claimed">{{ t('PointsRequestSent') }}: {{ claimedPoints }} {{ t('pts') }}</span>
			<span class="points-claim-btn__text" v-else>{{ t('Claim') }}</span>
		</v-btn>

		<PointsClaimDialog v-model="dialogOpen" :promo-name="promoName" :calculated-points="points" :loading="loading" :error="claimError" :overlay-target="overlayTarget" @confirm="handleConfirm" />
	</div>
</template>

<script>
// Module-scope (shared across every PointsClaimButton instance on the page — e.g. the legacy
// view renders a desktop AND a mobile zone for the same offer). Guards against both zones
// reacting to the same 'appointments-updated' event and submitting the same pending claim twice.
const submittingKeys = new Set();
</script>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import PointsClaimDialog from './PointsClaimDialog.vue';
import { claimPoints, getPointsHistory, refreshPoints, matchesOfferContext } from '@/api/sales/points';
import { enqueueOperation } from '@/sync/offlineSyncQueue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { eventBus } from '@/utils/eventBus';
import { useTranslator } from '@/utils/useTranslator';
import { log } from '@/utils/vueLogger';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// A "real" server uid is a non-empty GUID that is neither the empty guid nor a local
// temp uid (negative timestamp string used before the appointment is synced).
function isRealUid(uid) {
	if (!uid) return false;
	const s = String(uid);
	return s !== EMPTY_GUID && !s.startsWith('-');
}

const t = useTranslator();

const props = defineProps({
	campaignId: { type: [String, Number], default: null },
	offerId: { type: [String, Number], default: null },
	appointmentUid: { type: String, default: null },
	deliveryLocationId: { type: [String, Number], default: null },
	promoName: { type: String, default: '' },
	points: { type: Number, default: 0 },
	overlayTarget: { type: Object, default: null },
	campaignOfferStartDate: { type: String, default: null },
	campaignOfferEndDate: { type: String, default: null },
	readonly: { type: Boolean, default: false },
});

const STORAGE_KEY = 'points-pending-claims';

const dialogOpen = ref(false);
const loading = ref(false);
const claimed = ref(false);
const claimedPoints = ref(0);
const claimError = ref(null);

function pendingKey() {
	return `${props.deliveryLocationId}:${props.campaignId}:${props.offerId ?? 'null'}:${props.campaignOfferStartDate ?? 'null'}:${props.campaignOfferEndDate ?? 'null'}`;
}

function offerContext() {
	return {
		campaignId: props.campaignId,
		offerId: props.offerId,
		campaignOfferStartDate: props.campaignOfferStartDate,
		campaignOfferEndDate: props.campaignOfferEndDate,
		deliveryLocationId: props.deliveryLocationId,
	};
}

function loadPendingClaims() {
	try {
		return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
	} catch {
		return {};
	}
}

function savePendingClaim(intent) {
	const all = loadPendingClaims();
	all[pendingKey()] = intent;
	localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

function removePendingClaim() {
	const all = loadPendingClaims();
	delete all[pendingKey()];
	localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

function getPendingClaim() {
	return loadPendingClaims()[pendingKey()] ?? null;
}

function openDialog() {
	if (props.readonly && !claimed.value) return;
	claimError.value = null;
	dialogOpen.value = true;
}

// Re-claim guard: if a PENDING claim already exists for this store+campaign+offer+period,
// reflect it instead of allowing a 2nd claim. Also restore any locally-stored pending intent.
onMounted(async () => {
	// Restore pending intent from localStorage (claim stored but not yet submitted)
	const pending = getPendingClaim();
	if (pending) {
		claimed.value = true;
		claimedPoints.value = pending.pointsClaimed;
	}

	let historyChecked = false;
	let foundOnServer = false;
	try {
		const history = await getPointsHistory();
		historyChecked = true;
		const existing = history?.find((e) => e.status === 'PENDING' && matchesOfferContext(e, offerContext()));
		if (existing) {
			foundOnServer = true;
			claimed.value = true;
			claimedPoints.value = existing.pointsClaimed;
			// If the claim is already on the server, clear any stale localStorage entry
			removePendingClaim();
		}
	} catch {
		// non-blocking guard — ignore failures
	}

	// A local pending claim is only submitted once the backing visit is validated (see
	// handleAppointmentValidated below). If that already happened in a previous session
	// (the appointment already has a real uid) but the server has no matching record, the
	// validation event won't fire again — retry the submission now instead of leaving the
	// button stuck forever showing a claim that was never actually sent.
	if (pending && historyChecked && !foundOnServer && isRealUid(props.appointmentUid)) {
		await submitPendingClaim(props.appointmentUid, pending);
	}

	// Visit validation is signalled through the legacy amplify 'AppointmentUpdated' event,
	// relayed onto the Vue eventBus by iframeWatcher. We can't use a window CustomEvent here:
	// the legacy view runs in the iframe window while this component runs in the parent window.
	eventBus.on('appointments-updated', handleAppointmentValidated);
});

onBeforeUnmount(() => {
	eventBus.off('appointments-updated', handleAppointmentValidated);
});

async function handleAppointmentValidated(data) {
	const pending = getPendingClaim();
	if (!pending) return;

	// Only handle if this zone belongs to the validated visit's delivery location (when known)
	const evtDeliveryLocationId = data?.deliveryLocationId;
	if (evtDeliveryLocationId != null && String(evtDeliveryLocationId) !== String(props.deliveryLocationId)) {
		log('warn', 'Points', `⚠️ appointments-updated ignored: event deliveryLocationId=${evtDeliveryLocationId} vs zone deliveryLocationId=${props.deliveryLocationId}`);
		return;
	}

	// Resolve the freshest appointment uid: prefer the just-validated visit's uid (post-synch
	// server GUID), fall back to props. Null when still a temp/empty uid.
	const uid = isRealUid(data?.appointmentUid) ? data.appointmentUid : isRealUid(props.appointmentUid) ? props.appointmentUid : null;

	log('info', 'Points', `📨 appointments-updated received for pending claim ${pending.pointsClaimed}pts, resolved uid=${uid}`);
	await submitPendingClaim(uid, pending);
}

async function submitPendingClaim(uid, pending) {
	const key = pendingKey();
	// Another zone instance for this same offer (e.g. the sibling desktop/mobile zone) is
	// already submitting this exact pending claim — don't fire a duplicate request.
	if (submittingKeys.has(key)) return;
	submittingKeys.add(key);

	try {
		const payload = {
			campaignId: props.campaignId,
			offerId: props.offerId,
			appointmentUid: uid,
			deliveryLocationId: props.deliveryLocationId,
			pointsClaimed: pending.pointsClaimed,
			comment: pending.comment,
			campaignOfferStartDate: pending.campaignOfferStartDate,
			campaignOfferEndDate: pending.campaignOfferEndDate,
		};
		const label = `Points ${pending.pointsClaimed} pts – ${props.promoName}`;

		// Offline, or the visit isn't synced yet (temp/empty uid): defer via offlineSyncQueue so it's
		// submitted automatically once we're back online (SYNC_COMPLETED processes the queue).
		if (!iframeDataBridge.isOnline() || !uid) {
			await enqueueOperation('POINTS_CLAIM', payload, label);
			removePendingClaim();
			claimedPoints.value = pending.pointsClaimed;
			claimed.value = true;
			log('info', 'Points', `📥 Claim queued for sync: ${pending.pointsClaimed} pts`);
			return;
		}

		// Online with a real appointment uid: submit immediately.
		loading.value = true;
		claimError.value = null;
		try {
			await claimPoints(payload);
			removePendingClaim();
			claimedPoints.value = pending.pointsClaimed;
			claimed.value = true;
			log('success', 'Points', `✅ Submitted deferred claim: ${pending.pointsClaimed} pts`);
		} catch (error) {
			claimError.value = error.message === 'NO_ACTIVE_PERIOD' ? t('NoActiveScoringPeriod') : t('ClaimError');
			log('warn', 'Points', `⚠️ Deferred claimPoints error: ${error.message}`);
		} finally {
			loading.value = false;
		}
	} finally {
		submittingKeys.delete(key);
	}
}

function handleConfirm({ pointsClaimed, comment }) {
	// Store the intent locally — actual API call is deferred to visit validation
	savePendingClaim({
		campaignId: props.campaignId,
		offerId: props.offerId,
		deliveryLocationId: props.deliveryLocationId,
		pointsClaimed,
		comment,
		campaignOfferStartDate: props.campaignOfferStartDate,
		campaignOfferEndDate: props.campaignOfferEndDate,
	});

	dialogOpen.value = false;
	claimedPoints.value = pointsClaimed;
	claimed.value = true;
	log('info', 'Points', `📝 Points claim intent stored: ${pointsClaimed} pts (pending visit validation)`);
}
</script>
